import React, { useMemo, useState } from 'react';
import { format, parse, isSameDay, addDays } from 'date-fns';
import { formatDDMMYYYY, formatPtWeekday } from '../utils/dateI18n';
import { shouldShowTaskOnDate } from '../utils/taskRecurrence';
import { ArrowLeft, Calendar, CheckCircle, Clock, User, LogOut, Plus, X, Package, Map as MapIcon, List, Edit } from 'lucide-react';
import { roundAndSetDuration } from '../utils/timeUtils';
import { UserRole } from '../types/index';
import ModalDetalhesMovimento from '../paginas/movimentos/ModalDetalhesMovimento';

export default function SimpleTasksView({ 
  tasks, 
  movements = [],
  team = [], 
  currentUser,
  currentCompany,
  setTasks,
  onBack, 
  onTaskClick, 
  onNewTask, 
  onLogout,
  onEditMovement
}) {
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null); // Task being viewed in details modal
  const [editingTask, setEditingTask] = useState(null); // Task being edited
  const [selectedSection, setSelectedSection] = useState(null); // 'deliveries', 'create', 'map', 'all'
  const [selectedDay, setSelectedDay] = useState('today'); // 'today' or 'tomorrow'
  const [viewingMovement, setViewingMovement] = useState(null);
  const [taskForm, setTaskForm] = useState({
    name: '',
    notes: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    assignedTo: currentUser?.id || '',
    status: 'A Fazer'
  });

  // Reiniciar formulário
  const resetForm = () => {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    const defaultTime = roundAndSetDuration(currentTime);
    
    setTaskForm({
      name: '',
      notes: '',
      date: format(now, 'yyyy-MM-dd'),
      startTime: defaultTime.startTime,
      endTime: defaultTime.endTime,
      assignedTo: currentUser?.id || '',
      status: 'A Fazer'
    });
  };

  // Entregas dos movimentos (deliveryStatus ou hasDelivery para compatibilidade)
  // Excluir entregas já marcadas como entregues
  const deliveryMovements = useMemo(() => {
    return (movements || []).filter(m => {
      // Excluir se já foi entregue
      if (m.deliveryStatus === 'DELIVERED') return false;
      // Incluir se tem deliveryStatus (PENDING) ou hasDelivery (compatibilidade)
      return m.deliveryStatus || m.hasDelivery;
    }).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Most recent first
    });
  }, [movements]);

  // All tasks (excluir tarefas concluídas)
  const allTasks = useMemo(() => {
    return (tasks || [])
      .filter(task => !task.isCompleted && task.status !== 'Concluído')
      .sort((a, b) => {
        if (!a.date || !b.date) return 0;
        const dateA = parse(a.date, 'yyyy-MM-dd', new Date());
        const dateB = parse(b.date, 'yyyy-MM-dd', new Date());
        return dateB - dateA; // Most recent first
      });
  }, [tasks]);

  // Obter tarefas para o dia selecionado (hoje ou amanhã)
  const selectedDate = useMemo(() => {
    return selectedDay === 'today' ? new Date() : addDays(new Date(), 1);
  }, [selectedDay]);
  
  const dayTasks = useMemo(() => {
    const selectedStr = format(selectedDate, 'yyyy-MM-dd');
    return (tasks || [])
      .filter(task => {
        if (!task.date) return isSameDay(selectedDate, new Date());
        if (!shouldShowTaskOnDate(task, selectedStr)) return false;
        if (task.autoSchedule && task.repeatType) return true;
        return !task.isCompleted && task.status !== 'Concluído';
      })
      .sort((a, b) => {
        const at = (a?.startTime || '99:99');
        const bt = (b?.startTime || '99:99');
        return at.localeCompare(bt);
      });
  }, [tasks, selectedDate]);

  const getAssignedName = (task) => {
    if (!task?.assignedTo) return 'Não atribuído';
    return team.find(u => u.id === task.assignedTo)?.name || 'Não atribuído';
  };

  const assignableTeam = (team || []).filter(u => u?.role !== UserRole.ADMIN);


  // Tratar clique em criar tarefa
  const handleCreateTask = () => {
    resetForm();
    setEditingTask(null);
    setShowTaskModal(true);
  };

  // Tratar clique em tarefa - mostrar detalhes
  const handleTaskClick = (task) => {
    setViewingTask(task);
  };

  // Atualizar status (conclusão por ocorrência em tarefas recorrentes)
  const handleStatusChange = (task, newStatus) => {
    if (!setTasks || !currentCompany) return;
    const occurrenceStr = format(selectedDate, 'yyyy-MM-dd');
    const markingCompleted = newStatus === 'Concluído';
    const isRecurring = task.autoSchedule && task.repeatType;

    setTasks(prev => prev.map(t => {
      if (t.id !== task.id) return t;
      const updated = { ...t, status: newStatus, companyId: currentCompany.id };
      if (isRecurring) {
        const list = Array.isArray(t.completedOccurrences) ? [...t.completedOccurrences] : [];
        if (markingCompleted) { if (!list.includes(occurrenceStr)) list.push(occurrenceStr); updated.completedOccurrences = list; }
        else updated.completedOccurrences = list.filter(d => d !== occurrenceStr);
        updated.isCompleted = false;
        delete updated.completedDate;
      } else {
        updated.isCompleted = markingCompleted;
        if (markingCompleted) updated.completedDate = occurrenceStr;
        else delete updated.completedDate;
      }
      return updated;
    }));

    if (markingCompleted && isRecurring) {
      setViewingTask(prev => prev ? { ...prev, completedOccurrences: [...(prev.completedOccurrences || []), occurrenceStr] } : null);
    } else if (!markingCompleted && isRecurring) {
      setViewingTask(prev => prev ? { ...prev, completedOccurrences: (prev.completedOccurrences || []).filter(d => d !== occurrenceStr) } : null);
    } else {
      setViewingTask(prev => prev ? { ...prev, status: newStatus, isCompleted: markingCompleted, ...(markingCompleted ? { completedDate: occurrenceStr } : {}) } : null);
    }
  };

  // Tratar editar tarefa a partir da visualização
  const handleEditFromView = (task) => {
    setViewingTask(null);
    setEditingTask(task);
    
    // Preencher formulário com dados da tarefa
    const taskStartTime = task.startTime || '09:00';
    const { startTime: roundedStart, endTime: calculatedEnd } = roundAndSetDuration(taskStartTime);
    
    setTaskForm({
      name: task.name || '',
      notes: task.notes || '',
      date: task.date || format(new Date(), 'yyyy-MM-dd'),
      startTime: roundedStart,
      endTime: task.endTime || calculatedEnd,
      assignedTo: task.assignedTo || currentUser?.id || '',
      status: task.status || 'A Fazer'
    });
    setShowTaskModal(true);
  };

  // Tratar guardar tarefa (atualizar ou criar)
  const handleUpdateTask = (e) => {
    e.preventDefault();
    if (!setTasks || !currentCompany) return;
    
    if (editingTask) {
      // Atualizar tarefa existente
      setTasks(prev => prev.map(t => 
        t.id === editingTask.id 
          ? { ...t, ...taskForm, companyId: currentCompany.id, isCompleted: taskForm.status === 'Concluído' }
          : t
      ));
    } else {
      // Criar nova tarefa
      const newTask = {
        id: `task-${Date.now()}`,
        companyId: currentCompany.id,
        ...taskForm,
        isCompleted: taskForm.status === 'Concluído'
      };
      setTasks(prev => [...prev, newTask]);
    }
    
    setShowTaskModal(false);
    setEditingTask(null);
    resetForm();
  };

  const TaskCard = ({ task }) => (
    <div
      onClick={() => handleTaskClick(task)}
      className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 mb-1 truncate">{task.name}</p>
          {task.notes && (
            <p className="text-sm text-slate-500 mb-2 line-clamp-2">{task.notes}</p>
          )}
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 ${
          task.isCompleted || task.status === 'Concluído' 
            ? 'bg-green-100 text-green-700' 
            : task.status === 'Em Progresso'
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {task.status || 'A Fazer'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        {task.date && (() => {
          try {
            const taskDate = parse(task.date, 'yyyy-MM-dd', new Date());
            const isToday = isSameDay(taskDate, new Date());
            return (
              <div className="flex items-center gap-1">
                <Calendar size={12} />
                <span className={isToday ? 'font-bold text-green-600' : ''}>
                  {isToday ? 'Hoje' : formatDDMMYYYY(taskDate)}
                </span>
              </div>
            );
          } catch {
            return null;
          }
        })()}
        {task.startTime && (
          <div className="flex items-center gap-1">
            <Clock size={12} />
            <span>{task.endTime ? `${task.startTime} - ${task.endTime}` : task.startTime}</span>
          </div>
        )}
        {task.assignedTo && (
          <div className="flex items-center gap-1">
            <User size={12} />
            <span>{getAssignedName(task)}</span>
          </div>
        )}
      </div>
    </div>
  );

  const MovementCard = ({ movement }) => (
    <div 
      onClick={() => setViewingMovement(movement)}
      className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 mb-1 truncate">{movement.client || 'Consumidor Final'}</p>
          {movement.products && movement.products.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {movement.products.map((prod, idx) => (
                <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                  {prod.name}
                  {prod.ref && ` (Ref: ${prod.ref})`}
                </span>
              ))}
            </div>
          )}
          {movement.product && (
            <p className="text-sm text-slate-500 mt-2">{movement.product}</p>
          )}
        </div>
        <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0">
          Entrega
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mt-2">
        <div className="flex items-center gap-1">
          <Calendar size={12} />
          <span>{formatDDMMYYYY(new Date(movement.date))}</span>
        </div>
        {movement.locality && (
          <div className="flex items-center gap-1">
            <MapIcon size={12} />
            <span>{movement.locality}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Renderizar conteúdo com base na secção selecionada
  const renderContent = () => {
    if (!selectedSection) return null;

    switch (selectedSection) {
      case 'deliveries':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Entregas</h2>
              <button
                onClick={() => setSelectedSection(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-600" />
              </button>
            </div>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {deliveryMovements.length > 0 ? (
                deliveryMovements.map(movement => (
                  <MovementCard key={movement.id} movement={movement} />
                ))
              ) : (
                <div className="p-12 text-center">
                  <Package size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-lg font-bold text-slate-600 mb-2">Sem entregas</p>
                  <p className="text-sm text-slate-400">Nenhuma entrega encontrada</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'create':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Criar Tarefa</h2>
              <button
                onClick={() => setSelectedSection(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-600" />
              </button>
            </div>
            <div className="flex flex-col items-center justify-center min-h-[300px] space-y-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <Plus size={40} className="text-blue-600" />
              </div>
              <button
                onClick={handleCreateTask}
                className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm text-lg"
              >
                <CheckCircle size={20} /> Nova Tarefa
              </button>
              <p className="text-sm text-slate-400 text-center max-w-md">
                Crie uma nova tarefa para organizar o seu trabalho e acompanhar as entregas
              </p>
            </div>
          </div>
        );

      case 'map':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Mapa Diário</h2>
              <button
                onClick={() => setSelectedSection(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-600" />
              </button>
            </div>
            
            {/* Day selector */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setSelectedDay('today')}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                  selectedDay === 'today'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Hoje
              </button>
              <button
                onClick={() => setSelectedDay('tomorrow')}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                  selectedDay === 'tomorrow'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Amanhã
              </button>
            </div>

            {/* Selected date display */}
            <div className="mb-6 p-4 bg-slate-50 rounded-xl">
              <p className="text-base font-bold text-slate-900">
                {new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' }).format(selectedDate)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {formatPtWeekday(selectedDate)}
              </p>
            </div>

            {/* Tasks list */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {dayTasks.length > 0 ? (
                dayTasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-semibold text-slate-900 flex-1">{task.name}</p>
                      {task.startTime && (
                        <span className="text-sm font-bold text-slate-600 whitespace-nowrap">
                          {task.startTime}
                          {task.endTime && ` - ${task.endTime}`}
                        </span>
                      )}
                    </div>
                    {task.notes && (
                      <p className="text-sm text-slate-500 mb-2 line-clamp-2">{task.notes}</p>
                    )}
                    {task.assignedTo && (
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <User size={12} />
                        <span>{getAssignedName(task)}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-12 text-center">
                  <Calendar size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-lg font-bold text-slate-600 mb-2">Sem tarefas para este dia</p>
                  <p className="text-sm text-slate-400">Crie uma nova tarefa para começar</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'all':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Todas as Tarefas</h2>
              <button
                onClick={() => setSelectedSection(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-600" />
              </button>
            </div>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {allTasks.length > 0 ? (
                allTasks.map(task => (
                  <TaskCard key={task.id} task={task} />
                ))
              ) : (
                <div className="p-12 text-center">
                  <List size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-lg font-bold text-slate-600 mb-2">Sem tarefas</p>
                  <p className="text-sm text-slate-400">Crie uma nova tarefa para começar</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f1f8e9] p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {selectedSection ? (
              <button
                onClick={() => setSelectedSection(null)}
                className="p-2 hover:bg-white rounded-xl transition-colors bg-white shadow-sm"
              >
                <ArrowLeft size={24} className="text-slate-600" />
              </button>
            ) : (
              <button
                onClick={onBack}
                className="p-2 hover:bg-white rounded-xl transition-colors bg-white shadow-sm"
              >
                <ArrowLeft size={24} className="text-slate-600" />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Tarefas</h1>
              <p className="text-sm text-slate-500 mt-1">Gestão de tarefas e entregas</p>
            </div>
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="p-3 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
              title="Sair"
            >
              <LogOut size={20} className="text-slate-600" />
            </button>
          )}
        </div>

        {/* Cards Grid - Same style as SimpleDashboardView */}
        {!selectedSection ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-xs grid grid-cols-2 gap-4">
              {/* Entregas Card */}
              <div 
                onClick={() => setSelectedSection('deliveries')}
                className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-emerald-300 transition-all active:scale-[0.98]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-3">
                    <Package size={28} className="text-emerald-600" />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Entregas</p>
                  <p className="text-2xl font-black text-slate-900">{deliveryMovements.length}</p>
                </div>
              </div>

              {/* Criar Tarefa Card */}
              <div 
                onClick={() => setSelectedSection('create')}
                className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-blue-300 transition-all active:scale-[0.98]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-3">
                    <Plus size={28} className="text-blue-600" />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Criar Tarefa</p>
                  <p className="text-2xl font-black text-slate-900">+</p>
                </div>
              </div>

              {/* Mapa Diário Card */}
              <div 
                onClick={() => setSelectedSection('map')}
                className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-purple-300 transition-all active:scale-[0.98]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-3">
                    <MapIcon size={28} className="text-purple-600" />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Mapa Diário</p>
                  <p className="text-2xl font-black text-slate-900">{dayTasks.length}</p>
                </div>
              </div>

              {/* Todas as Tarefas Card */}
              <div 
                onClick={() => setSelectedSection('all')}
                className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-cyan-300 transition-all active:scale-[0.98]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mb-3">
                    <List size={28} className="text-cyan-600" />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Todas as Tarefas</p>
                  <p className="text-2xl font-black text-slate-900">{allTasks.length}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          renderContent()
        )}
      </div>

      {/* Movement Details Modal */}
      <ModalDetalhesMovimento
        movimento={viewingMovement}
        onClose={() => {
          setViewingMovement(null);
          // Se estiver na seção de entregas, fechar também a seção
          if (selectedSection === 'deliveries') {
            setSelectedSection(null);
          }
        }}
        onMarkAsDelivered={viewingMovement ? async () => {
          if (onEditMovement) {
            try {
              await onEditMovement({
                ...viewingMovement,
                deliveryStatus: 'DELIVERED'
              });
              // Fechar modal e voltar para a tela principal
              setViewingMovement(null);
              setSelectedSection(null);
            } catch (error) {
              console.error('Erro ao marcar como entregue:', error);
            }
          }
        } : undefined}
      />

      {/* Task Details Modal */}
      {viewingTask && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Detalhes da Tarefa</h2>
              <button
                type="button"
                onClick={() => setViewingTask(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-500 block mb-1">Nome da Tarefa</label>
                <p className="text-lg font-semibold text-slate-900">{viewingTask.name || 'Sem título'}</p>
              </div>

              {viewingTask.notes && (
                <div>
                  <label className="text-sm font-bold text-slate-500 block mb-1">Notas</label>
                  <p className="text-base text-slate-700 whitespace-pre-wrap">{viewingTask.notes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {viewingTask.date && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Data</label>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">
                        {(() => {
                          try {
                            const taskDate = parse(viewingTask.date, 'yyyy-MM-dd', new Date());
                            const isToday = isSameDay(taskDate, new Date());
                            return isToday ? 'Hoje' : formatDDMMYYYY(taskDate);
                          } catch {
                            return viewingTask.date;
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                )}

                {(viewingTask.startTime || viewingTask.endTime) && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Horário</label>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">
                        {viewingTask.startTime || '—'}
                        {viewingTask.endTime ? ` - ${viewingTask.endTime}` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {viewingTask.assignedTo && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Atribuído a</label>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">{getAssignedName(viewingTask)}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-bold text-slate-500 block mb-1">Status</label>
                  <select
                    value={viewingTask.status || 'A Fazer'}
                    onChange={(e) => handleStatusChange(viewingTask, e.target.value)}
                    className={`w-full p-3 border-2 rounded-xl font-bold text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors ${
                      viewingTask.isCompleted || viewingTask.status === 'Concluído' 
                        ? 'bg-green-100 text-green-700 border-green-300' 
                        : viewingTask.status === 'Em Progresso'
                        ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                        : 'bg-blue-100 text-blue-700 border-blue-300'
                    }`}
                  >
                    <option value="A Fazer">A Fazer</option>
                    <option value="Em Progresso">Em Progresso</option>
                    <option value="Concluído">Concluído</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setViewingTask(null)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => handleEditFromView(viewingTask)}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Edit size={18} />
                Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Creation/Edit Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form onSubmit={handleUpdateTask} className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowTaskModal(false);
                  setEditingTask(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Nome da Tarefa *</label>
                <input
                  type="text"
                  required
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Ex: Entrega para cliente X"
                />
              </div>

              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Notas</label>
                <textarea
                  value={taskForm.notes}
                  onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Detalhes adicionais..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Data *</label>
                  <input
                    type="date"
                    required
                    value={taskForm.date}
                    onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Horário Início</label>
                  <input
                    type="time"
                    value={taskForm.startTime}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      const { startTime: roundedStart, endTime: calculatedEnd } = roundAndSetDuration(newStartTime);
                      setTaskForm({ 
                        ...taskForm, 
                        startTime: roundedStart,
                        endTime: calculatedEnd
                      });
                    }}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Horário Fim</label>
                  <input
                    type="time"
                    value={taskForm.endTime}
                    onChange={(e) => setTaskForm({ ...taskForm, endTime: e.target.value })}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Atribuído a</label>
                  <select
                    value={taskForm.assignedTo}
                    onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">Não atribuído</option>
                    {assignableTeam
                      .filter(u => u?.role === UserRole.EMPLOYEE || u?.role === UserRole.OWNER)
                      .map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Status</label>
                <select
                  value={taskForm.status}
                  onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="A Fazer">A Fazer</option>
                  <option value="Em Progresso">Em Progresso</option>
                  <option value="Concluído">Concluído</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowTaskModal(false);
                  setEditingTask(null);
                  resetForm();
                }}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors"
              >
                {editingTask ? 'Atualizar' : 'Criar'} Tarefa
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}